import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { onAuthStateChanged } from "firebase/auth";
import auth from "./auth";
import LoginModal from "./LoginModal";

// Import local fallback background image
import back1 from "./back1.png";  // We'll still use this as a default

const CarDetailsForRent = () => {
  const { carID } = useParams();
  const navigate = useNavigate();

  // NEW: We'll store the dynamic background URL here, defaulting to back1
  const [bgURL, setBgURL] = useState(back1);

  const [carDetails, setCarDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch the background document (ID: "1") and set bgURL if available
  useEffect(() => {
    const fetchBackground = async () => {
      try {
        const docRef = doc(db, "background", "1");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // If there's a 'picture' field, use it; otherwise fallback to back1
          if (data.picture) {
            setBgURL(data.picture);
          }
        }
      } catch (err) {
        console.error("Error fetching background:", err);
      }
    };
    fetchBackground();
  }, []);

  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        const carDocSnap = await getDoc(doc(db, "cars", carID));
        if (carDocSnap.exists()) {
          setCarDetails(carDocSnap.data());
        } else {
          console.error("Car not found");
        }
      } catch (error) {
        console.error("Error fetching car details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCarDetails();
  }, [carID]);

  if (loading) {
    return <p style={{ color: "white", textAlign: "center" }}>Loading...</p>;
  }

  if (!carDetails) {
    return <p style={{ color: "red", textAlign: "center" }}>Car not found.</p>;
  }

  const { carName, pictures, price, location, about, features } = carDetails;

  const handleRentCar = () => {
    if (auth.currentUser) {
      navigate(`/rental/${carID}`);
    } else {
      setModalOpen(true);
    }
  };

  const handleInvest = () => {
    navigate(`/cars/${carID}`);
  };

  return (
    <div
      style={{
        // CHANGED: Fade to white near the bottom + dynamic background from Firestore
        backgroundImage: `linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 85%
        ), url(${bgURL})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top",
        minHeight: "100vh",
        margin: 0,
        padding: "20px",
        color: "white",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header Section with Back Button */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
            position: "relative",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              padding: "10px 15px",
              fontSize: "1rem",
              fontWeight: "bold",
              color: "white",
              backgroundColor: "#007bff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            &larr; Back
          </button>
          <h1 style={{ fontSize: "2.5rem", color: "white", margin: "0" }}>
            {carName}
          </h1>
          <p
            style={{
              fontSize: "1.2rem",
              color: "#e1e1e1",
              margin: "5px 0 0",
            }}
          >
            {location}
          </p>
        </div>

        {/* Carousel Section */}
        <div
          style={{
            marginBottom: "40px",
            maxWidth: "800px",
            margin: "0 auto 20px",
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          <Carousel
            showThumbs={false}
            infiniteLoop
            autoPlay
            interval={5000}
            emulateTouch
            stopOnHover
            showArrows
          >
            {pictures && pictures.length > 0 ? (
              pictures.map((pic, index) => (
                <div key={index}>
                  <img
                    src={pic}
                    alt={`Slide ${index + 1}`}
                    style={{
                      maxHeight: "400px",
                      objectFit: "cover",
                      margin: "0 auto",
                      borderRadius: "10px",
                      marginBottom: "30px",
                    }}
                  />
                </div>
              ))
            ) : (
              <div>
                <img
                  src="https://via.placeholder.com/800x400"
                  alt="Placeholder"
                />
                <p>No images available</p>
              </div>
            )}
          </Carousel>
        </div>

        {/* Details and Booking Section */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            justifyContent: "space-between",
          }}
        >
          {/* About Section */}
          <div
            style={{
              flex: "1",
              minWidth: "300px",
              backgroundColor: "#1e1e1e",
              padding: "20px",
              borderRadius: "10px",
            }}
          >
            <h2 style={{ color: "white", marginBottom: "10px" }}>
              About the Car
            </h2>
            <p>{about || "No description available."}</p>

            <h3 style={{ color: "white", marginTop: "20px" }}>Features:</h3>
            <ul>
              {features && features.length > 0 ? (
                features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: "5px" }}>
                    {feature}
                  </li>
                ))
              ) : (
                <li>No features listed.</li>
              )}
            </ul>

            {/* Invest Now Section */}
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <p>
                This vehicle is available to invest in. Own the car you rent,
                get free days, discounts, and use it when you need it.
              </p>
              <button
                onClick={handleInvest}
                style={{
                  padding: "10px 15px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  color: "white",
                  backgroundColor: "#28a745",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
              >
                Invest Now
              </button>
            </div>
          </div>

          {/* Booking Section */}
          <div
            style={{
              flex: "1",
              minWidth: "300px",
              padding: "20px",
              backgroundColor: "#1e1e1e",
              borderRadius: "10px",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <h2
              style={{
                color: "white",
                marginBottom: "10px",
                fontSize: "2rem",
                textAlign: "center",
              }}
            >
              150 Miles per day included
            </h2>
            <p
              style={{
                fontSize: "1.8rem",
                fontWeight: "bold",
                marginBottom: "0px",
                color: "#4caf50",
              }}
            >
              Price Per Day: ${price}
            </p>
            <p
              style={{
                fontSize: "0.9rem",
                color: "#7a7a7a",
                marginBottom: "20px",
              }}
            >
              *Excludes taxes and fees
            </p>
            <button
              onClick={handleRentCar}
              style={{
                width: "80%",
                padding: "15px 20px",
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: "white",
                backgroundColor: "#007bff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Continue
            </button>
          </div>
        </div>

        {/* Google Maps Section */}
        <div
          style={{
            marginTop: "40px",
            backgroundColor: "#1e1e1e",
            padding: "20px",
            borderRadius: "10px",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "white", marginBottom: "10px" }}>
            Pick-Up Location
          </h2>
          <p style={{ color: "#b0b0b0", marginBottom: "20px" }}>
            6000 Eldorado Parkway, Frisco, TX 75033
          </p>
          <iframe
            title="Pickup Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3349.007575584546!2d-96.83519178487558!3d33.16219848086525!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c105e8b8ec0c7%3A0x4e64cf7f8a36c7e1!2s6000%20Eldorado%20Pkwy%2C%20Frisco%2C%20TX%2075033!5e0!3m2!1sen!2sus!4v1677982344482!5m2!1sen!2sus"
            style={{
              border: "0",
              width: "100%",
              height: "300px",
              borderRadius: "10px",
            }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </div>
      </div>
      <LoginModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        afterLoginSuccess={() => navigate(`/rental/${carID}`)}
      />
    </div>
  );
};

export default CarDetailsForRent;
